import type { ListWishlistsQuery } from '@nexus/api-types';
import { inject, Injectable } from '@angular/core';
import type {
  ListWishlistInvitationsResponse,
  ListWishlistsOverviewResponse,
  PublicWishlist,
  PublicWishlistResolveRequest,
  WishlistAccessPolicyResponse,
  WishlistManagerDetail,
} from '@nexus/api-types';
import type {
  WishlistAccessPolicyViewModel,
  WishlistDetailViewModel,
  WishlistInvitedGuestViewModel,
  WishlistOverviewViewModel,
  WishlistPublicViewModel,
} from '@nexus/customer/wishlists/model';
import { catchError, map, of, shareReplay, startWith, type Observable } from 'rxjs';
import { mapWishlistRequestError } from '../errors/map-wishlist-request-error';
import {
  mapWishlistAccessPolicyResponseToViewModel,
  mapWishlistInvitationsResponseToViewModels,
  mapWishlistManagerDetailToViewModel,
  mapWishlistPublicDtoToViewModel,
  mapWishlistsOverviewResponseToViewModel,
} from '../mappers/wishlist-view-model.mapper';
import type { WishlistRequestState } from '../types/wishlist-request-state.type';
import { WishlistApiClientService } from './wishlist-api-client.service';

@Injectable({
  providedIn: 'root',
})
export class WishlistQueriesService {
  private readonly apiClient: WishlistApiClientService = inject(WishlistApiClientService);

  public listWishlistsOverview(query: ListWishlistsQuery = {}): Observable<WishlistRequestState<WishlistOverviewViewModel>> {
    return this.toRequestState(
      this.apiClient
        .listWishlistsOverview(query)
        .pipe(
          map(
            (response: ListWishlistsOverviewResponse): WishlistOverviewViewModel =>
              mapWishlistsOverviewResponseToViewModel(response),
          ),
        ),
    );
  }

  public getWishlistDetail(wishlistId: string): Observable<WishlistRequestState<WishlistDetailViewModel>> {
    return this.toRequestState(
      this.apiClient
        .getWishlistDetail(wishlistId)
        .pipe(
          map(
            (response: WishlistManagerDetail): WishlistDetailViewModel =>
              mapWishlistManagerDetailToViewModel(response),
          ),
        ),
    );
  }

  public getAccessPolicy(wishlistId: string): Observable<WishlistRequestState<WishlistAccessPolicyViewModel>> {
    return this.toRequestState(
      this.apiClient
        .getAccessPolicy(wishlistId)
        .pipe(
          map(
            (response: WishlistAccessPolicyResponse): WishlistAccessPolicyViewModel =>
              mapWishlistAccessPolicyResponseToViewModel(response),
          ),
        ),
    );
  }

  public listInvitedGuests(
    wishlistId: string,
  ): Observable<WishlistRequestState<readonly WishlistInvitedGuestViewModel[]>> {
    return this.toRequestState(
      this.apiClient
        .listInvitedGuests(wishlistId)
        .pipe(
          map(
            (response: ListWishlistInvitationsResponse): readonly WishlistInvitedGuestViewModel[] =>
              mapWishlistInvitationsResponseToViewModels(response),
          ),
        ),
    );
  }

  public resolvePublicWishlist(
    request: PublicWishlistResolveRequest,
  ): Observable<WishlistRequestState<WishlistPublicViewModel>> {
    return this.toRequestState(
      this.apiClient
        .resolvePublicWishlist(request)
        .pipe(
          map((response: PublicWishlist): WishlistPublicViewModel => mapWishlistPublicDtoToViewModel(response)),
        ),
    );
  }

  private toRequestState<TData>(source: Observable<TData>): Observable<WishlistRequestState<TData>> {
    return source.pipe(
      map((data: TData): WishlistRequestState<TData> => createSuccessState(data)),
      startWith(createLoadingState<TData>()),
      catchError((error: unknown): Observable<WishlistRequestState<TData>> => of(createErrorState<TData>(error))),
      shareReplay({ bufferSize: 1, refCount: true }),
    );
  }
}

function createLoadingState<TData>(): WishlistRequestState<TData> {
  return {
    status: 'loading',
    data: null,
    error: null,
  };
}

function createSuccessState<TData>(data: TData): WishlistRequestState<TData> {
  return {
    status: 'success',
    data,
    error: null,
  };
}

function createErrorState<TData>(error: unknown): WishlistRequestState<TData> {
  return {
    status: 'error',
    data: null,
    error: mapWishlistRequestError(error),
  };
}
